import { useQuery } from '@tanstack/react-query';
import type { MediaRef, SeoMeta, TranslatableString } from '@geoic/shared';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';
import { Input } from '@/components/ui/Field.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { RichTextInput } from '@/components/form/RichTextInput.js';
import { MediaPicker } from '@/components/media/MediaPicker.js';
import { MediaGalleryPicker } from '@/components/media/MediaGalleryPicker.js';
import { MultiSelect } from '@/features/blocks/BlockFields.js';
import { queryKeys } from '@/lib/query-keys.js';
import { ContentListPage } from '../ContentListPage.js';
import { ContentEditPage } from '../ContentEditPage.js';
import { SeoPanel, emptySeo } from '../SeoPanel.js';
import { createContentApi, type ContentRecord } from '../content-api.js';
import { servicesApi } from '../services/ServicesPages.js';

export type ProjectRecord = ContentRecord & {
  title: TranslatableString;
  shortDescription: TranslatableString;
  description: TranslatableString;
  coverImage: MediaRef | null;
  gallery: MediaRef[];
  client: TranslatableString;
  location: TranslatableString;
  year: number | null;
  serviceIds: string[];
  seo: SeoMeta;
};

export const projectsApi = createContentApi<ProjectRecord>('/projects');

function useServiceOptions() {
  const params = { limit: 100, sort: 'title' as const };
  const query = useQuery({
    queryKey: queryKeys.content.list('services', { ...params, purpose: 'options' }),
    queryFn: ({ signal }) => servicesApi.list(params, signal),
    staleTime: 5 * 60 * 1000,
  });

  return (query.data?.data ?? []).map((row) => ({
    value: row._id,
    label: row.title?.uz || row.slug || row._id,
  }));
}

export function ProjectsListPage() {
  return (
    <ContentListPage<ProjectRecord>
      title="Loyihalar"
      collection="projects"
      contentApi={projectsApi}
      basePath="/projects"
      createLabel="Loyiha qo‘shish"
      emptyTitle="Hozircha loyiha yo‘q"
      columns={[
        {
          key: 'title',
          header: 'Nomi',
          render: (row) => (
            <span className="font-medium text-n-800">{row.title?.uz || '(nomsiz)'}</span>
          ),
        },
        {
          key: 'client',
          header: 'Buyurtmachi',
          hideOnTablet: true,
          render: (row) => <span className="text-n-600">{row.client?.uz || '—'}</span>,
        },
        {
          key: 'year',
          header: 'Yil',
          align: 'right',
          width: '90px',
          render: (row) => <span className="text-n-600">{row.year ?? '—'}</span>,
        },
      ]}
    />
  );
}

type ProjectForm = {
  title: TranslatableString;
  slug: string;
  shortDescription: TranslatableString;
  description: TranslatableString;
  coverImage: MediaRef | null;
  gallery: MediaRef[];
  client: TranslatableString;
  location: TranslatableString;
  year: string;
  serviceIds: string[];
  seo: SeoMeta;
};

export function ProjectEditPage() {
  const services = useServiceOptions();

  return (
    <ContentEditPage<ProjectForm>
      collection="projects"
      contentApi={projectsApi}
      basePath="/projects"
      backLabel="Loyihalar"
      newTitle="Yangi loyiha"
      titleOf={(form) => form.title.uz}
      empty={() => ({
        title: { uz: '', ru: '', en: '' },
        slug: '',
        shortDescription: { uz: '', ru: '', en: '' },
        description: { uz: '', ru: '', en: '' },
        coverImage: null,
        gallery: [],
        client: { uz: '', ru: '', en: '' },
        location: { uz: '', ru: '', en: '' },
        year: '',
        serviceIds: [],
        seo: emptySeo(),
      })}
      fromRecord={(record) => {
        const project = record as ProjectRecord;
        return {
          title: project.title ?? { uz: '', ru: '', en: '' },
          slug: project.slug ?? '',
          shortDescription: project.shortDescription ?? { uz: '', ru: '', en: '' },
          description: project.description ?? { uz: '', ru: '', en: '' },
          coverImage: project.coverImage ?? null,
          gallery: project.gallery ?? [],
          client: project.client ?? { uz: '', ru: '', en: '' },
          location: project.location ?? { uz: '', ru: '', en: '' },
          year: project.year ? String(project.year) : '',
          serviceIds: project.serviceIds ?? [],
          seo: project.seo ?? emptySeo(),
        };
      }}
      toInput={(form) => ({
        title: form.title,
        slug: form.slug,
        shortDescription: form.shortDescription,
        description: form.description,
        coverImage: form.coverImage,
        gallery: form.gallery,
        client: form.client,
        location: form.location,
        year: form.year === '' ? null : Number(form.year),
        serviceIds: form.serviceIds,
        seo: form.seo,
      })}
      renderFields={({ form, setForm, fieldErrors }) => (
        <div className="grid gap-4 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <Card>
              <CardHeader title="Loyiha" />
              <CardBody className="space-y-4">
                <TranslatableInput
                  label="Nomi"
                  required
                  value={form.title}
                  onChange={(title) => setForm((current) => ({ ...current, title }))}
                  error={fieldErrors['title.uz']}
                />
                <TranslatableInput
                  label="Qisqa tavsif"
                  multiline
                  rows={2}
                  value={form.shortDescription}
                  onChange={(shortDescription) =>
                    setForm((current) => ({ ...current, shortDescription }))
                  }
                />
                <RichTextInput
                  label="To‘liq tavsif"
                  value={form.description}
                  onChange={(description) => setForm((current) => ({ ...current, description }))}
                />
              </CardBody>
            </Card>

            <Card>
              <CardHeader title="Galereya" />
              <CardBody>
                <MediaGalleryPicker
                  label="Loyiha rasmlari"
                  value={form.gallery}
                  onChange={(gallery) => setForm((current) => ({ ...current, gallery }))}
                />
              </CardBody>
            </Card>

            <SeoPanel
              value={form.seo}
              onChange={(seo) => setForm((current) => ({ ...current, seo }))}
              fallbackTitle={form.title.uz || undefined}
            />
          </div>

          <Card>
            <CardHeader title="Ma’lumotlar" />
            <CardBody className="space-y-4">
              <MediaPicker
                label="Muqova rasmi"
                value={form.coverImage}
                onChange={(coverImage) => setForm((current) => ({ ...current, coverImage }))}
              />
              <TranslatableInput
                label="Buyurtmachi"
                value={form.client}
                onChange={(client) => setForm((current) => ({ ...current, client }))}
              />
              <TranslatableInput
                label="Joylashuv"
                value={form.location}
                onChange={(location) => setForm((current) => ({ ...current, location }))}
              />
              <Input
                type="number"
                label="Yil"
                min={1990}
                max={2100}
                value={form.year}
                onChange={(event) =>
                  setForm((current) => ({ ...current, year: event.target.value }))
                }
              />
              <MultiSelect
                label="Bog‘langan xizmatlar"
                max={10}
                selected={form.serviceIds}
                options={services}
                onChange={(serviceIds) => setForm((current) => ({ ...current, serviceIds }))}
                hint="Xizmat sahifasida shu loyiha namuna sifatida chiqadi"
              />
              <Input
                label="Manzil (slug)"
                value={form.slug}
                onChange={(event) =>
                  setForm((current) => ({ ...current, slug: event.target.value }))
                }
                error={fieldErrors.slug}
              />
            </CardBody>
          </Card>
        </div>
      )}
    />
  );
}
